import { useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/whatsapp";
import ellenAvatar from "@/assets/team-ellen.webp";
import { useI18n } from "@/lib/i18n";

const buildUrl = (msg: string) =>
  `https://wa.me/31641674914?text=${encodeURIComponent(msg)}`;

const WhatsAppButton = () => {
  const [open, setOpen] = useState(false);
  const { lang } = useI18n();

  const copy = lang === "nl"
    ? {
        name: "Ellen",
        role: "A-WAVE",
        greeting: "Hi, leuk je te zien!",
        body: "Welkom bij A-WAVE. We helpen technische bedrijven groeien met marketing, events en strategie. Waar kan ik je mee helpen?",
        ask: "Stuur een bericht via WhatsApp:",
        quick: [
          { label: "Plan een kennismaking", msg: "Hi Ellen, ik wil graag een kennismaking plannen." },
          { label: "Meer weten over jullie aanpak", msg: "Hi Ellen, ik wil meer weten over de A-WAVE aanpak." },
          { label: "Ik heb een andere vraag", msg: "Hi Ellen, ik heb een vraag over A-WAVE." },
        ],
        label: "Chat met Ellen",
        powered: "WhatsApp",
      }
    : {
        name: "Ellen",
        role: "A-WAVE",
        greeting: "Hi, great to see you!",
        body: "Welcome to A-WAVE. We help technical companies grow with marketing, events and strategy. How can I help?",
        ask: "Send a message via WhatsApp:",
        quick: [
          { label: "Book an intro call", msg: "Hi Ellen, I would like to book an intro call." },
          { label: "Learn about your approach", msg: "Hi Ellen, I would like to learn more about the A-WAVE approach." },
          { label: "I have another question", msg: "Hi Ellen, I have a question about A-WAVE." },
        ],
        label: "Chat with Ellen",
        powered: "WhatsApp",
      };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Chat panel */}
      <div
        className={`absolute bottom-20 right-0 w-[320px] sm:w-[360px] origin-bottom-right transition-all duration-300 ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-black/5">
          {/* Header */}
          <div className="bg-brand-black px-4 py-3 flex items-center gap-3">
            <div className="relative">
              <img
                src={ellenAvatar}
                alt="Ellen"
                className="w-10 h-10 rounded-full object-cover border-2 border-white/20"
              />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-brand-black" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-semibold leading-tight">{copy.name}</div>
              <div className="text-white/60 text-xs leading-tight">{copy.role}</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="text-white/70 hover:text-white transition-colors p-1"
            >
              <X size={18} />
            </button>
          </div>

          {/* Message bubble */}
          <div className="p-4 bg-neutral-50">
            <div className="bg-white rounded-lg p-3 shadow-sm border border-black/5">
              <div className="text-neutral-900 text-sm font-semibold mb-1">{copy.greeting}</div>
              <p className="text-neutral-700 text-sm leading-relaxed">{copy.body}</p>
            </div>

            {/* Mini testimonial */}
            <div className="mt-3 bg-white/60 rounded-lg p-3 border border-black/5">
              <div className="flex gap-0.5 mb-1.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span key={i} className="text-primary text-[11px]">★</span>
                ))}
              </div>
              <p className="text-neutral-700 text-xs leading-relaxed italic">
                {lang === "nl"
                  ? "\"Ze doet wat ze zegt, denkt mee en lost problemen op voordat ze ontstaan.\""
                  : "\"She does what she says, thinks along and solves problems before they happen.\""}
              </p>
              <div className="text-neutral-500 text-[10px] mt-1.5">
                — Gerben de Jong, Vitamunda BV
              </div>
            </div>

            <div className="text-neutral-500 text-xs mt-4 mb-2 font-medium">{copy.ask}</div>

            {/* Quick replies */}
            <div className="flex flex-col gap-2">
              {copy.quick.map((q) => (
                <a
                  key={q.label}
                  href={buildUrl(q.msg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white hover:bg-neutral-900 hover:text-white text-neutral-900 text-sm font-medium px-3 py-2 rounded-lg border border-black/10 shadow-sm transition-colors text-left"
                >
                  {q.label}
                </a>
              ))}
            </div>

            <div className="mt-3 text-right text-[10px] text-neutral-400 flex items-center justify-end gap-1">
              Powered by <span className="font-semibold text-[#25D366]">{copy.powered}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating pill with avatar + name */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={copy.label}
        className="relative group flex items-center"
      >
        {!open && (
          <span className="hidden sm:flex items-center gap-2 mr-[-14px] pl-3 pr-5 py-2 bg-brand-black text-white text-xs font-semibold rounded-full shadow-lg border border-white/10 whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            {lang === "nl" ? "App met Ellen" : "Chat with Ellen"}
          </span>
        )}
        <span className="relative inline-flex">
          {!open && (
            <>
              <span className="absolute inset-1 rounded-full bg-[#25D366] animate-ping opacity-30" />
              <span className="absolute -inset-1 rounded-full bg-[#25D366]/20 animate-pulse" />
            </>
          )}
          {!open ? (
            <span className="relative flex items-center justify-center bg-[#25D366] hover:bg-[#20bd5a] p-1 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 border-2 border-[#25D366]">
              <img
                src={ellenAvatar}
                alt="Ellen"
                className="w-12 h-12 rounded-full object-cover"
              />
            </span>
          ) : (
            <span className="relative flex items-center justify-center bg-[#25D366] hover:bg-[#20bd5a] text-white p-4 rounded-full shadow-lg transition-all hover:scale-110">
              <X size={28} />
            </span>
          )}
        </span>
      </button>

      {/* Hidden direct WhatsApp link (kept for accessibility/SEO) */}
      <a href={WHATSAPP_URL} className="sr-only" aria-hidden="true" tabIndex={-1}>
        WhatsApp
      </a>
    </div>
  );
};

export default WhatsAppButton;
